import { Component, HostListener } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from 'src/app/helpers/services/api.service';
import { HttpService } from 'src/app/helpers/services/http.service';
import { PublicService } from 'src/app/helpers/services/public.service';
import { ToastresService } from 'src/app/helpers/services/toaster.service';
import { Attachment } from '../../../models/attachment.model';
import { RejectComponent } from '../reject/reject.component';

@Component({
  selector: 'app-file-management',
  templateUrl: './file-management.component.html',
  styleUrls: ['./file-management.component.css'],
})
export class FileManagementComponent {
  public selectedId: number;
  public attachments: Attachment[] = [];
  public currentIndex: number = 0;
  public viewingFileMode: boolean = false;
  public sanitizedUrl: SafeResourceUrl;
  public fileType: string;
  public title: string = 'View Files';
  public path: string;

  public workspaceProjectId: number = 0;
  public projectId: number = 0;
  public storyId: number = 0;
  public taskId: number = 0;
  public meetingId: number = 0;
  public contractId: number = 0;
  public invoiceId: number = 0;

  public contractFlowStatus: number = 0;
  public canViewFileInfo: boolean = true;

  fileTypes = [
    {
      keywords: ['pdf'],
      viewerUrl: (url: string) => url,
    },
    {
      keywords: ['word', 'msword', 'doc', 'wordprocessingml'],
      viewerUrl: (url: string) =>
        `https://docs.google.com/gview?url=${url}&embedded=true`,
    },
    {
      keywords: ['excel', 'spreadsheetml', 'csv', 'xls'],
      viewerUrl: (url: string) =>
        `https://view.officeapps.live.com/op/view.aspx?src=${url}`,
    },
    {
      keywords: ['ppt', 'presentationml'],
      viewerUrl: (url: string) =>
        `https://view.officeapps.live.com/op/view.aspx?src=${url}`,
    },
    {
      keywords: [
        'text',
        'plain',
        'json',
        'xml',
        'html',
        'markdown',
        'md',
        'javascript',
        'css',
        'x-python',
        'x-c',
        'x-java',
      ],
      viewerUrl: (url: string) => url,
    },
    {
      keywords: ['video', 'mp4', 'webm', 'ogg'],
      viewerUrl: (url: string) => url,
    },
    {
      keywords: ['audio', 'wav', 'mp3'],
      viewerUrl: (url: string) => url,
    },
    {
      keywords: ['svg+xml'],
      viewerUrl: (url: string) => url,
    },
  ];

  constructor(
    public publicService: PublicService,
    public activeModal: NgbActiveModal,
    private api: ApiService,
    private sanitizer: DomSanitizer,
    private httpService: HttpService,
    private toastrsService: ToastresService,
    private ngbModal: NgbModal,
  ) {}

  ngOnInit() {
    if (this.attachments.length === 1) {
      this.currentIndex = 0;

      if (this.invoiceId || this.contractId) {
        this.openFile(this.attachments[0]);
      } else if (this.canViewFileInfo) {
        const currentFile = this.attachments[0];
        if (this.isMediaFile(currentFile.file_type)) {
          this.openFile(currentFile);
        }
      }
    } else if (this.selectedId) {
      this.currentIndex = this.attachments.findIndex(
        (att) => att.id === this.selectedId,
      );
      if (this.currentIndex === -1) {
        this.currentIndex = 0;
      }

      if (this.canViewFileInfo && this.attachments.length > 0) {
        const currentFile = this.attachments[this.currentIndex];
        if (this.isMediaFile(currentFile.file_type)) {
          this.openFile(currentFile);
        }
      }
    }

    this.updateTitle();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.attachments.length <= 1) return;

    if (event.key === 'ArrowRight' || event.key === 'Right') {
      event.preventDefault();
      this.navigateNext();
    } else if (event.key === 'ArrowLeft' || event.key === 'Left') {
      event.preventDefault();
      this.navigatePrev();
    }
  }

  navigateNext() {
    if (this.currentIndex < this.attachments.length - 1) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0;
    }
    this.updateAfterNavigation();
  }

  navigatePrev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = this.attachments.length - 1;
    }
    this.updateAfterNavigation();
  }

  navigateToIndex(index: number) {
    this.currentIndex = index;
    this.updateAfterNavigation();
  }

  updateAfterNavigation() {
    this.updateTitle();

    // Update carousel
    const carousel = document.querySelector('#carouselExampleCaptions');
    if (carousel) {
      const bsCarousel =
        (window as any).bootstrap.Carousel.getInstance(carousel) ||
        new (window as any).bootstrap.Carousel(carousel);
      bsCarousel.to(this.currentIndex);
    }

    // Handle view mode
    if (this.canViewFileInfo && this.attachments.length > 0) {
      const currentFile = this.attachments[this.currentIndex];
      if (this.isMediaFile(currentFile.file_type)) {
        this.openFile(currentFile);
      } else {
        this.viewingFileMode = false;
      }
    }
  }

  updateTitle() {
    if (this.attachments.length > 0) {
      this.title = this.attachments[this.currentIndex].original_file_name;
    }
  }

  openFile(attachment: Attachment) {
    const fileType = attachment.file_type;
    this.fileType = fileType;

    if (this.publicService.isDisplayableImage(fileType)) {
      this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        attachment.stored_file_name,
      );
      this.viewingFileMode = true;
    } else if (fileType.startsWith('audio/')) {
      this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        attachment.stored_file_name,
      );
      this.viewingFileMode = true;
    } else if (fileType.startsWith('video/')) {
      this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        attachment.stored_file_name,
      );
      this.viewingFileMode = true;
    } else if (this.canOpenFile(fileType)) {
      this.sanitizedUrl = this.getSanitizedUrl(
        attachment.stored_file_name,
        fileType,
      );
      this.viewingFileMode = true;
    }
  }

  getSanitizedUrl(fileUrl: string, fileType: string): SafeResourceUrl {
    const matchingType = this.fileTypes.find((type) =>
      type.keywords.some((keyword) => fileType.includes(keyword)),
    );

    if (matchingType) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        matchingType.viewerUrl(fileUrl),
      );
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl('');
  }

  canOpenFile(fileType: string | undefined): boolean {
    if (!fileType) return false;

    const disallowedTypes = [
      'zip',
      'rar',
      '7z',
      'x-7z-compressed',
      'x-rar-compressed',
    ];
    if (disallowedTypes.some((ext) => fileType.includes(ext))) {
      return false;
    }

    return this.fileTypes.some((type) =>
      type.keywords.some((keyword) => fileType.includes(keyword)),
    );
  }

  closeViewer() {
    this.viewingFileMode = false;
  }

  download(attachment: any) {
    let apiUrl = '';

    if (this.invoiceId) {
      apiUrl = this.api.invoices.download(this.invoiceId);
    } else if (this.contractId) {
      apiUrl = this.api.contracts.download(this.contractId);
    } else if (this.workspaceProjectId) {
      apiUrl = this.api.attachments.download.workspaceProject(attachment.id);
    } else if (this.projectId) {
      apiUrl = this.api.attachments.download.project(attachment.id);
    } else if (this.storyId) {
      apiUrl = this.api.attachments.download.story(attachment.id);
    } else if (this.taskId) {
      apiUrl = this.api.attachments.download.task(attachment.id);
    } else if (this.meetingId) {
      apiUrl = this.api.attachments.download.meeting(attachment.id);
    }

    if (apiUrl) {
      window.open(apiUrl, '_blank');
    }
  }

  getFileType(fileType: string): string {
    if (this.publicService.isDisplayableImage(fileType)) {
      return 'image';
    } else if (fileType.startsWith('audio/')) {
      return 'audio';
    } else {
      return 'other';
    }
  }

  getFileExtension(file: Attachment): string {
    if (file.file_type.startsWith('application/pdf')) {
      return 'PDF';
    } else if (
      file.file_type.startsWith(
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      )
    ) {
      return 'Excel';
    } else if (
      file.file_type.startsWith(
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      )
    ) {
      return 'Word';
    } else {
      const fileNameParts = file.original_file_name.split('.');
      return fileNameParts[fileNameParts.length - 1].toUpperCase();
    }
  }

  approveContract() {
    this.httpService
      .action(this.api.contracts.approve(this.contractId), { status })
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.activeModal.close();
            this.toastrsService.ShowSuccess(res.msg);
          } else {
            this.toastrsService.ShowError(res.msg);
          }
        },
      });
  }

  rejectContract() {
    const modalRef = this.ngbModal.open(RejectComponent, { centered: true });
    modalRef.componentInstance.id = this.contractId;
    modalRef.componentInstance.type = 'contract';
    modalRef.result.then(() => this.activeModal.close());
  }

  getUploadedByName(): string {
    const currentAttachment = this.attachments[this.currentIndex];
    if (currentAttachment?.insert_user.dual_name) {
      return currentAttachment.insert_user.dual_name;
    } else if (currentAttachment?.insert_user?.user_name) {
      return currentAttachment.insert_user.user_name;
    }
    return 'Unknown User';
  }

  getUploadedDate(): string {
    const currentAttachment = this.attachments[this.currentIndex];
    if (!currentAttachment?.insert_date) {
      return '';
    }
    const date = new Date(currentAttachment.insert_date);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  getFormattedFileSize(): string {
    const currentAttachment = this.attachments[this.currentIndex];
    return currentAttachment?.file_size || '0 B';
  }

  isMediaFile(fileType?: string): boolean {
    if (!fileType) return false;
    return (
      this.publicService.isDisplayableImage(fileType) ||
      fileType.startsWith('audio/') ||
      fileType.startsWith('video/')
    );
  }

  isImage(fileType?: string): boolean {
    if (!fileType) return false;
    return this.publicService.isDisplayableImage(fileType);
  }

  getViewButtonText(fileType?: string): string {
    if (!fileType) return 'ViewFile';

    if (this.publicService.isDisplayableImage(fileType)) {
      return 'ViewImage';
    } else if (fileType.startsWith('audio/')) {
      return 'ViewAudio';
    } else if (fileType.startsWith('video/')) {
      return 'ViewVideo';
    } else {
      return 'ViewFile';
    }
  }

  toggleInfoView(): void {
    if (!this.canViewFileInfo) return;

    const currentAttachment = this.attachments[this.currentIndex];
    if (this.viewingFileMode) {
      // Currently viewing file, switch to info box
      this.viewingFileMode = false;
    } else {
      // Currently showing info box, switch to file view
      this.openFile(currentAttachment);
    }
  }

  onSlideChange(event: any) {
    this.currentIndex = event.to;
    this.updateTitle();

    // Reset view mode when sliding to new attachment
    if (this.canViewFileInfo && this.attachments.length > 0) {
      const currentFile = this.attachments[this.currentIndex];
      if (this.isMediaFile(currentFile.file_type)) {
        this.openFile(currentFile);
      } else {
        this.viewingFileMode = false;
      }
    }
  }
}
